import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  PhotoIcon,
  CalendarIcon,
  DocumentTextIcon,
  ArchiveBoxIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { transactionApi } from '../../api/transactionApi';
import { invoiceApi } from '../../api/invoiceApi';
import { customerApi } from '../../api/customerApi';
import { productApi } from '../../api/productApi';
import { uploadFile, convertToBase64 } from '../../utils/fileUpload';

const AddTransaction = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type'); // 'credit' or 'payment'
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(null);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  
  // Tab State
  const [activeTab, setActiveTab] = useState('cash'); // 'cash' | 'bill'

  // Bill State
  const [items, setItems] = useState([
    { id: Date.now(), productId: '', name: '', quantity: 1, unitPrice: 0, total: 0 }
  ]);
  const [taxAmount, setTaxAmount] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [linkToLedger, setLinkToLedger] = useState(true);
  
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [quantity, setQuantity] = useState(1);

  const isEditMode = searchParams.get('edit') === 'true';
  const transactionId = searchParams.get('transactionId');
  const fromPage = searchParams.get('from');
  const goBackUrl = fromPage === 'transactions' ? '/transactions' : `/customers/${id}/ledger`;

  useEffect(() => {
    if (!type || (type !== 'credit' && type !== 'payment' && type !== 'debit')) {
      navigate(`/customers/${id}/ledger`);
    }
    fetchCustomer();
    if (isEditMode && transactionId) {
      fetchTransactionDetails();
    }
    fetchProducts();
  }, [id, type, isEditMode, transactionId]);

  const fetchProducts = async () => {
    try {
      const res = await productApi.getProducts();
      setProducts(res.data.data || []);
    } catch (error) {
      console.error('Failed to fetch products', error);
    }
  };

  const [invoiceId, setInvoiceId] = useState(null);

  const fetchTransactionDetails = async () => {
    try {
      const res = await transactionApi.getTransactionDetails(transactionId);
      const tx = res.data.data;
      setAmount(tx.amount.toString());
      setDescription(tx.description || '');
      setDate(new Date(tx.createdAt).toISOString().split('T')[0]);
      if (tx.attachments && tx.attachments.length > 0) {
        setAttachments(tx.attachments);
      }
      
      // If linked to an invoice, fetch it and switch to bill tab
      if (tx.reference && tx.reference.invoiceId) {
        setInvoiceId(tx.reference.invoiceId);
        setActiveTab('bill');
        try {
          const invRes = await invoiceApi.getInvoiceDetails(tx.reference.invoiceId);
          const inv = invRes.data.data;
          if (inv.items && inv.items.length > 0) {
            setItems(inv.items.map(item => ({
              ...item,
              id: item._id || Date.now() + Math.random()
            })));
          }
          setTaxAmount(inv.taxAmount || 0);
          setDiscountAmount(inv.discountAmount || 0);
          if (inv.notes) setDescription(inv.notes);
        } catch (e) {
          console.error('Failed to fetch invoice details', e);
        }
      }
    } catch (error) {
      toast.error('Failed to load transaction details');
    }
  };

  const fetchCustomer = async () => {
    try {
      const res = await customerApi.getCustomerDetails(id);
      setCustomer(res.data.data);
    } catch (error) {
      toast.error('Failed to load customer details');
    }
  };

  // ---- Bill Logic ----
  const addItem = () => {
    setItems([
      ...items,
      { id: Date.now(), productId: '', name: '', quantity: 1, unitPrice: 0, total: 0 }
    ]);
  };

  const removeItem = (idToRemove) => {
    if (items.length === 1) return;
    setItems(items.filter(item => item.id !== idToRemove));
  };

  const handleItemChange = (idToUpdate, field, value) => {
    setItems(items.map(item => {
      if (item.id === idToUpdate) {
        const updatedItem = { ...item, [field]: value };
        if (field === 'productId') {
          const selectedProduct = products.find(p => p._id === value);
          if (selectedProduct) {
            updatedItem.name = selectedProduct.name;
            updatedItem.unitPrice = selectedProduct.price;
          }
        }
        updatedItem.total = (Number(updatedItem.quantity) || 0) * (Number(updatedItem.unitPrice) || 0);
        return updatedItem;
      }
      return item;
    }));
  };

  const subtotal = items.reduce((sum, item) => sum + item.total, 0);
  const grandTotal = subtotal + Number(taxAmount) - Number(discountAmount);

  const handleSaveBill = async () => {
    const validItems = items.filter(i => i.name.trim() !== '' && i.quantity > 0 && i.unitPrice > 0);
    if (validItems.length === 0) {
      toast.error('Please add at least one valid item');
      return;
    }
    try {
      setLoading(true);
      const payload = {
        customerId: id,
        type: 'sale',
        ledgerType: type, // 'credit' or 'payment'
        items: validItems,
        taxAmount: Number(taxAmount),
        discountAmount: Number(discountAmount),
        linkToLedger,
        issueDate: new Date(date),
        notes: description
      };
      
      if (isEditMode && invoiceId) {
        await invoiceApi.updateInvoice(invoiceId, payload);
        toast.success('Bill Updated Successfully!');
      } else {
        await invoiceApi.createInvoice(payload);
        toast.success('Bill Created Successfully!');
      }
      
      navigate(goBackUrl);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create bill');
      setLoading(false);
    }
  };
  // --------------------

  const handleSave = async () => {
    if (activeTab === 'bill') {
      return handleSaveBill();
    }
    let finalAmount = amount;
    try {
      if (amount && /[+\-*/]/.test(amount)) {
        // eslint-disable-next-line no-new-func
        const evaluated = new Function(`'use strict'; return (${amount})`)();
        if (!isNaN(evaluated) && isFinite(evaluated)) {
          finalAmount = evaluated.toString();
        }
      }
    } catch (e) {}

    if (!finalAmount || Number(finalAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      setLoading(true);
      
      const payload = {
        customerId: id,
        type,
        amount: Number(finalAmount),
        description,
        date
      };

      if (isCredit && selectedProduct) {
        payload.productId = selectedProduct;
        payload.quantity = quantity;
      }

      if (attachments && attachments.length > 0) {
        toast.loading(`Uploading ${attachments.filter(a => a instanceof File).length > 0 ? 'files' : 'data'}...`, { id: 'upload' });
        
        const finalAttachments = [];
        for (const file of attachments) {
          if (file instanceof File) {
            const base64 = await convertToBase64(file);
            const url = await uploadFile(base64, 'transactions');
            if (url) {
              finalAttachments.push({
                name: file.name,
                url,
                type: file.type.includes('pdf') ? 'pdf' : 'image'
              });
            }
          } else {
            // It's already an uploaded file from DB
            finalAttachments.push(file);
          }
        }
        toast.dismiss('upload');
        payload.attachments = finalAttachments;
      }

      if (isEditMode) {
        await transactionApi.editTransaction(transactionId, payload);
        toast.success('Transaction updated successfully');
      } else {
        await transactionApi.addTransaction(payload);
        toast.success(type === 'credit' ? 'Credit recorded successfully' : 'Payment recorded successfully');
      }
      
      navigate(goBackUrl);
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Transaction failed';
      toast.error(errorMsg);
      setLoading(false);
    }
  };

  const handleProductSelect = (e) => {
    const prodId = e.target.value;
    setSelectedProduct(prodId);
    
    if (prodId) {
      const prod = products.find(p => p._id === prodId);
      if (prod) {
        setQuantity(1);
        setAmount(prod.price.toString());
        setDescription(`${prod.name} (Qty: 1)`);
      }
    } else {
      setQuantity(1);
      setDescription('');
    }
  };

  const handleQuantityChange = (e) => {
    const qty = parseInt(e.target.value) || 1;
    setQuantity(qty);
    if (selectedProduct) {
      const prod = products.find(p => p._id === selectedProduct);
      if (prod) {
        setAmount((prod.price * qty).toString());
        setDescription(`${prod.name} (Qty: ${qty})`);
      }
    }
  };

  const isCredit = type === 'credit';
  
  // Clean minimal theme colors
  const themeColor = isCredit ? 'text-[#ef4444]' : 'text-[#16a34a]';
  const themeBg = isCredit ? 'bg-[#ef4444]' : 'bg-[#16a34a]';
  const themeHover = isCredit ? 'hover:bg-[#dc2626]' : 'hover:bg-[#15803d]';
  const themeFocus = isCredit ? 'focus:border-red-500 focus:ring-1 focus:ring-red-500' : 'focus:border-green-500 focus:ring-1 focus:ring-green-500';

  return (
    <div className="w-full h-full flex flex-col bg-white dark:bg-gray-800 overflow-hidden px-4 sm:px-6 lg:px-10 -mt-4 sm:-mt-6 lg:-mt-10 pt-4 sm:pt-6 lg:pt-10 transition-colors">
      
      {/* Header */}
      <div className={`w-full flex items-center gap-4 px-2 py-5 ${isCredit ? 'border-b border-red-100 dark:border-red-900/50' : 'border-b border-green-100 dark:border-green-900/50'}`}>
        <Link 
          to={goBackUrl}
          className="p-2 -ml-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <ArrowLeftIcon className="h-6 w-6 text-gray-700 dark:text-gray-300" />
        </Link>
        <div>
          <p className={`text-[12px] font-extrabold uppercase tracking-widest ${themeColor}`}>
            {isEditMode ? 'Edit Transaction' : (isCredit ? 'You Gave To' : 'You Got From')}
          </p>
          <h1 className="text-xl font-extrabold text-gray-900 dark:text-white mt-0.5">
            {customer?.name || 'Loading...'}
          </h1>
        </div>
      </div>

      {/* Tabs (Hide only if editing a Cash entry, show for Bill edit) */}
      {(!isEditMode || invoiceId) && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex gap-4 overflow-x-auto">
          {!invoiceId && (
            <button 
              onClick={() => setActiveTab('cash')}
              className={`px-4 py-2 rounded-lg font-bold text-[13px] transition-colors whitespace-nowrap ${activeTab === 'cash' ? 'bg-[#ef4444] text-white shadow-sm' : 'text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-800'}`}
            >
              Cash Entry
            </button>
          )}
          <button 
            onClick={() => setActiveTab('bill')}
            className={`px-4 py-2 rounded-lg font-bold text-[13px] transition-colors whitespace-nowrap ${activeTab === 'bill' ? 'bg-[#ef4444] text-white shadow-sm' : 'text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-800'}`}
          >
            Itemized Bill
          </button>
        </div>
      )}

      {/* Main Form Area */}
      <div className={`py-6 sm:py-8 space-y-6 sm:space-y-8 flex-1 max-w-full mx-auto w-full ${activeTab === 'bill' ? 'overflow-y-auto' : ''}`}>
        
        {activeTab === 'cash' ? (
          <>
            {/* Amount Input */}

        <div>
          <label className="block text-[13px] font-bold text-gray-600 dark:text-gray-400 mb-2">Amount</label>
          <div className="relative flex flex-col">
            <div className="relative flex items-center">
              <span className={`absolute left-4 font-bold text-xl ${themeColor}`}>₹</span>
              <input
                type="text"
                value={amount}
                onChange={(e) => {
                  // Only allow numbers and basic math operators
                  const val = e.target.value.replace(/[^0-9+\-*/.]/g, '');
                  setAmount(val);
                }}
                onBlur={() => {
                  // Evaluate on blur to clean up the input
                  try {
                    if (amount && /[+\-*/]/.test(amount) && !/[+\-*/.]$/.test(amount)) {
                      // eslint-disable-next-line no-new-func
                      const evaluated = new Function(`'use strict'; return (${amount})`)();
                      if (!isNaN(evaluated) && isFinite(evaluated)) {
                        setAmount(evaluated.toString());
                      }
                    }
                  } catch (e) {}
                }}
                className={`block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 py-3 pl-10 pr-4 text-gray-900 dark:text-white text-2xl font-bold transition-all placeholder:text-gray-300 dark:placeholder:text-gray-500 outline-none ${themeFocus}`}
                placeholder="0"
                autoFocus
              />
            </div>
            {/* Show live calculation if it's an expression */}
            {amount && /[+\-*/]/.test(amount) && !/[+\-*/.]$/.test(amount) && (
              <div className="absolute -bottom-6 left-0 text-sm font-bold text-gray-500 dark:text-gray-400 flex items-center gap-2">
                <span>=</span>
                <span>
                  {(() => {
                    try {
                      // eslint-disable-next-line no-new-func
                      const res = new Function(`'use strict'; return (${amount})`)();
                      return isNaN(res) || !isFinite(res) ? '' : `₹${res}`;
                    } catch(e) { return ''; }
                  })()}
                </span>
              </div>
            )}
          </div>
        </div>



        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Details */}
          <div>
            <label className="block text-[13px] font-bold text-gray-600 dark:text-gray-400 mb-2">Details (Optional)</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <DocumentTextIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              </div>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={`block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 py-2.5 pl-10 pr-4 text-gray-900 dark:text-white text-[14px] outline-none transition-all ${themeFocus}`}
                placeholder="Items, Bill No, etc."
              />
            </div>
          </div>

          {/* Date */}
          <div>
            <label className="block text-[13px] font-bold text-gray-600 dark:text-gray-400 mb-2">Date</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <CalendarIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              </div>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={`block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 py-2.5 pl-10 pr-4 text-gray-900 dark:text-white text-[14px] outline-none transition-all ${themeFocus}`}
              />
            </div>
          </div>
        </div>

        {/* Attachments */}
        <div>
          <label className="block text-[13px] font-bold text-gray-600 dark:text-gray-400 mb-2">Attach Bills / Receipts (Optional)</label>
          <div className="relative">
            <input
              type="file"
              id="file-upload"
              accept="image/png, image/jpeg, image/webp, application/pdf"
              multiple
              onChange={(e) => {
                if(e.target.files.length > 0) {
                  setAttachments([...attachments, ...Array.from(e.target.files)]);
                }
              }}
              className="hidden"
            />
            
            <label 
              htmlFor="file-upload" 
              className={`flex items-center justify-center gap-2 w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-gray-600 dark:text-gray-400`}
            >
              <PhotoIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <span className="text-[14px] font-medium text-gray-500 dark:text-gray-400">
                Click to add images or PDFs
              </span>
            </label>

            {/* Inline Previews */}
            {attachments.length > 0 && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
                {attachments.map((file, idx) => {
                  const isFile = file instanceof File;
                  const previewUrl = isFile ? URL.createObjectURL(file) : file.url;
                  const isPdf = isFile ? file.type.includes('pdf') : file.type === 'pdf';
                  
                  return (
                    <div key={idx} className="rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm relative group aspect-square flex items-center justify-center">
                      {isPdf ? (
                        <div className="text-center p-2 cursor-pointer" onClick={() => setPreviewItem(file)}>
                          <DocumentTextIcon className="h-10 w-10 text-red-500 mx-auto mb-2" />
                          <p className="text-[11px] font-medium text-gray-500 truncate w-full px-2">{file.name}</p>
                        </div>
                      ) : (
                        <img 
                          src={previewUrl} 
                          alt="Attachment" 
                          onClick={() => setPreviewItem(file)}
                          className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity" 
                        />
                      )}
                      
                      <button
                        onClick={() => {
                          const newAtt = [...attachments];
                          newAtt.splice(idx, 1);
                          setAttachments(newAtt);
                        }}
                        className="absolute top-1.5 right-1.5 bg-white text-red-500 p-1 rounded-full shadow hover:bg-red-50 transition-colors opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
                        title="Remove"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
          </>
        ) : (
          <div className="space-y-8 pb-10">
            {/* Items List */}
            <div className="space-y-4">
              <div className="flex items-center justify-between pb-2 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-bold text-gray-800 dark:text-white">Items</h2>
                <button 
                  onClick={addItem}
                  className="flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  <PlusIcon className="w-4 h-4" /> Add Row
                </button>
              </div>

              {items.map((item) => (
                <div key={item.id} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_1fr_1fr_auto] gap-4 items-end bg-gray-50 dark:bg-gray-900/50 p-4 rounded-xl border border-gray-100 dark:border-gray-800 relative group">
                  <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[11px] font-bold text-gray-500 mb-1 uppercase tracking-wider">Product (Optional)</label>
                      <select
                        value={item.productId}
                        onChange={(e) => handleItemChange(item.id, 'productId', e.target.value)}
                        className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-[#ef4444] focus:border-[#ef4444] block p-2.5"
                      >
                        <option value="">-- Custom --</option>
                        {products.map(p => (
                          <option key={p._id} value={p._id}>{p.name} (₹{p.price})</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-gray-500 mb-1 uppercase tracking-wider">Item Details</label>
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => handleItemChange(item.id, 'name', e.target.value)}
                        placeholder="e.g. Petrol, Gas"
                        className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-[#ef4444] focus:border-[#ef4444] block p-2.5"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-gray-500 mb-1 uppercase tracking-wider">Qty</label>
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(item.id, 'quantity', e.target.value)}
                      className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-[#ef4444] focus:border-[#ef4444] block p-2.5"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-gray-500 mb-1 uppercase tracking-wider">Price (₹)</label>
                    <input
                      type="number"
                      min="0"
                      value={item.unitPrice}
                      onChange={(e) => handleItemChange(item.id, 'unitPrice', e.target.value)}
                      className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-[#ef4444] focus:border-[#ef4444] block p-2.5"
                    />
                  </div>
                  <div className="flex items-center justify-between md:justify-end gap-4 h-[42px]">
                    <div className="text-right">
                      <span className="block text-[11px] font-bold text-gray-500 mb-1 md:hidden uppercase tracking-wider">Total</span>
                      <span className="font-bold text-gray-900 dark:text-white">₹{item.total}</span>
                    </div>
                    {items.length > 1 && (
                      <button 
                        onClick={() => removeItem(item.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 p-2 rounded-lg transition-colors"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Calculations Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <label className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/50 rounded-xl cursor-pointer">
                  <input
                    type="checkbox"
                    checked={linkToLedger}
                    onChange={(e) => setLinkToLedger(e.target.checked)}
                    className="w-5 h-5 text-red-600 bg-white border-gray-300 rounded focus:ring-red-500 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <div>
                    <p className="font-bold text-red-900 dark:text-red-100">Add to Customer's Khata</p>
                    <p className="text-xs text-red-700 dark:text-red-300">Creates a "{isCredit ? 'You Gave' : 'You Got'}" transaction automatically.</p>
                  </div>
                </label>
              </div>

              <div className="bg-gray-50 dark:bg-gray-900/50 p-6 rounded-2xl border border-gray-100 dark:border-gray-800 space-y-4">
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 font-medium">
                  <span>Subtotal</span>
                  <span>₹{subtotal}</span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">Tax Amount (+)</span>
                  <div className="relative w-32">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                    <input
                      type="number"
                      value={taxAmount}
                      onChange={(e) => setTaxAmount(e.target.value)}
                      className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-red-500 focus:border-red-500 block pl-8 p-2 text-right"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">Discount (-)</span>
                  <div className="relative w-32">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                    <input
                      type="number"
                      value={discountAmount}
                      onChange={(e) => setDiscountAmount(e.target.value)}
                      className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-red-500 focus:border-red-500 block pl-8 p-2 text-right"
                    />
                  </div>
                </div>
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
                  <span className="text-lg font-bold text-gray-900 dark:text-white">Grand Total</span>
                  <span className="text-2xl font-extrabold text-[#ef4444] dark:text-red-400">₹{grandTotal}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="py-6 border-t border-gray-100 dark:border-gray-700 flex gap-4 max-w-4xl mx-auto w-full mt-auto">
        <Link 
          to={goBackUrl}
          className="flex-1 py-4 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-bold text-[15px] rounded-xl text-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors tracking-wide"
        >
          CANCEL
        </Link>
        <button 
          onClick={handleSave} 
          disabled={loading}
          className={`flex-[2] py-4 text-white font-bold text-[15px] rounded-xl shadow-md transition-colors uppercase tracking-widest ${themeBg} ${themeHover} ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
        >
          {loading ? 'SAVING...' : 'SAVE ENTRY'}
        </button>
      </div>

      {/* Image Preview Modal */}
      {previewItem && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-gray-900/90 backdrop-blur-sm" onClick={() => setPreviewItem(null)}></div>
          <div className="bg-transparent relative z-10 flex flex-col animate-in zoom-in-95 duration-200 max-w-5xl w-full h-full items-center justify-center">
            <button 
              onClick={() => setPreviewItem(null)} 
              className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors z-20"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-8 h-8">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            {(() => {
              const isFile = previewItem instanceof File;
              const previewUrl = isFile ? URL.createObjectURL(previewItem) : previewItem.url;
              const isPdf = isFile ? previewItem.type.includes('pdf') : previewItem.type === 'pdf';
              
              if (isPdf) {
                return (
                  <iframe 
                    src={previewUrl}
                    className="w-full h-[85vh] rounded-xl shadow-2xl bg-white"
                    title="Fullscreen PDF"
                  />
                );
              }
              return (
                <img 
                  src={previewUrl}
                  alt="Fullscreen Preview" 
                  className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
                />
              );
            })()}
          </div>
        </div>
      )}

    </div>
  );
};

export default AddTransaction;
